import { Component, inject, OnInit, DestroyRef, ViewChild, AfterViewInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { Platform } from 'src/app/models/api/common';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { RuntimeRiskData } from 'src/app/components/dashboards/charts/runtime-risk-breakdown/runtime-risk-breakdown.component';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { DashboardComponent } from 'src/app/views/dashboard/dashboard.component';
import { FindingsAggregatedByServiceSeverity } from 'src/app/models/api/finding.model';
import { take } from 'rxjs';
import { UserService } from 'src/app/services/infra/user/user.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { Labels } from 'src/app/labels/englishLabels';

@Component({
  selector: 'app-main-dashboard',
  templateUrl: './main-dashboard.component.html',
  styleUrls: ['./main-dashboard.component.scss'],
})
export class MainDashboardComponent implements OnInit, AfterViewInit {
  public threatModel:
    | 'mitreBySeverities'
    | 'owaspBySeverities'
    | 'owaspLlmBySeverities'
    | 'owaspAsiBySeverities'
    | 'owaspNhiBySeverities' = 'owaspLlmBySeverities';
  public featureFlagsService = inject(FeatureFlagsService);
  public featureFlagTypes = FeatureFlagTypes;

  public runtimeRiskData: RuntimeRiskData[] = [];
  public isLoadingRuntimeRiskData = false;
  public runtimeRiskDataError: string | null = null;

  @ViewChild('dashboard', { static: true }) dashboardComponent!: DashboardComponent;

  constructor(
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _logger: LoggerService,
    private readonly _destroyRef: DestroyRef,
    private readonly _userService: UserService,
    private readonly _notificationsService: NotificationsService
  ) {}

  private static readonly _REDIRECT_KEY = 'angular_react_redirect_ts';
  private static readonly _REDIRECT_COOLDOWN_MS = 5000;
  // Guards the brief window between reload() call and page unload
  private _isRedirecting = false;

  ngOnInit(): void {
    this._redirectToReact();
    if (this._isRedirecting) return;
    this._checkUserAccess();
    this._loadRuntimeRiskData();
  }

  private _redirectToReact(): void {
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(MainDashboardComponent._REDIRECT_KEY) ?? '0', 10);

    if (now - last < MainDashboardComponent._REDIRECT_COOLDOWN_MS) {
      sessionStorage.removeItem(MainDashboardComponent._REDIRECT_KEY);
      return;
    }

    this._isRedirecting = true;
    sessionStorage.setItem(MainDashboardComponent._REDIRECT_KEY, String(now));
    window.location.reload();
  }

  private _checkUserAccess(): void {
    // Wait for user data to be fully loaded before checking access
    this._userService
      .canLoadApp()
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe(() => {
        const userPermission = this._userService.userPermission;
        const userPermissions = this._userService.userPermissions;
        this._logger.info('User access check', { userPermission, userPermissions });

        // Check if user has no role permission (no access groups)
        const hasRolePermission = userPermissions.some((p) => p.startsWith('user:'));
        if (!hasRolePermission) {
          this._notificationsService.warning(Labels.accessRestricted.notification);
        }
      });
  }

  ngAfterViewInit(): void {
    if (this._isRedirecting) return;
    this._subscribeToFilterChanges();
  }

  private _subscribeToFilterChanges(): void {
    if (this.dashboardComponent?.selectedFilter$) {
      this.dashboardComponent.selectedFilter$.pipe(takeUntilDestroyed(this._destroyRef), take(1)).subscribe({
        next: (filters: ISelectedFilters) => {
          this._loadRuntimeRiskData(filters);
        },
        error: (error: unknown) => {
          this._logger.error('Error subscribing to filter changes', { error });
          this._loadRuntimeRiskData();
        },
      });
    } else {
      this._logger.warn('Dashboard component selectedFilter$ not available');
      // Try again after a short delay
      setTimeout(() => {
        if (this.dashboardComponent?.selectedFilter$) {
          this._subscribeToFilterChanges();
        }
      }, 100);
    }
  }

  private _loadRuntimeRiskData(filters?: ISelectedFilters): void {
    this.isLoadingRuntimeRiskData = true;
    this.runtimeRiskDataError = null;

    this._aiFindingsHttpService
      .getFindingsAggregatedByServiceSeverity(filters)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (data) => {
          this._logger.debug('Runtime risk data received:', data);
          this.runtimeRiskData = this._transformBackendDataToChartFormat(data);
          this.isLoadingRuntimeRiskData = false;
          this._logger.debug('Transformed runtime risk data:', this.runtimeRiskData);
        },
        error: () => {
          this.runtimeRiskDataError = 'Failed to load runtime risk data';
          this.isLoadingRuntimeRiskData = false;
          this.runtimeRiskData = [];
        },
      });
  }

  /**
   * Transform backend data format to chart-expected format
   * Backend: [{ service, severity, count }]
   * Chart: [{ service, critical?, high?, medium?, low?, informational? }]
   */
  private _transformBackendDataToChartFormat(backendData: FindingsAggregatedByServiceSeverity[]): RuntimeRiskData[] {
    this._logger.debug('Transforming backend data:', { dataLength: backendData?.length, data: backendData });

    if (!backendData || backendData.length === 0) {
      this._logger.debug('No backend data to transform');
      return [];
    }

    // Group data by service
    const serviceGroups = new Map<string, RuntimeRiskData>();

    backendData.forEach((item) => {
      if (!item.service) {
        this._logger.warn('Item missing service field:', item);
        return;
      }

      // Get or create service entry
      if (!serviceGroups.has(item.service)) {
        serviceGroups.set(item.service, { service: item.service });
      }

      const serviceData = serviceGroups.get(item.service)!;

      // Map severity to the appropriate property
      switch (item?.severity?.toLowerCase()) {
        case 'critical':
          serviceData.critical = item.count ?? 0;
          break;
        case 'high':
          serviceData.high = item.count ?? 0;
          break;
        case 'medium':
          serviceData.medium = item.count ?? 0;
          break;
        case 'low':
          serviceData.low = item.count ?? 0;
          break;
        case 'informational':
          serviceData.informational = item.count ?? 0;
          break;
        default:
          this._logger.warn(`Unknown severity level: ${item?.severity}`, item);
      }
    });

    const result = Array.from(serviceGroups.values());
    return result;
  }

  public currentFilterCategory() {
    if (this.threatModel === 'mitreBySeverities') return 'mitre';
    if (this.threatModel === 'owaspBySeverities') return 'owaspLcnc';
    if (this.threatModel === 'owaspLlmBySeverities') return 'owaspLlm';
    if (this.threatModel === 'owaspAsiBySeverities') return 'owaspAsi';
    if (this.threatModel === 'owaspNhiBySeverities') return 'owaspNhi';
    return 'No filter was found';
  }

  public platformLegendFormatter = (name: string): string => {
    const platformKey = Object.keys(Platform).find(
      (key) => Platform[key as keyof typeof Platform] === name
    ) as keyof typeof Platform;

    if (platformKey) {
      return getPlatformDisplayName(Platform[platformKey]);
    }

    return name;
  };
}
